import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronDown, MoreHorizontal } from "lucide-react";
import { IcCancel, DragHandleDots2, IcMapLocationPin, IcPlus } from "@racoon/icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@racoon/ui";
import { useTypedT, Namespace, TranslationKey } from "@racoon/i18n";
import { isDraftJobId } from "../../model/draft-jobs";

// Why a job may not go through: the selected truck is not equipped for this work
// (red), the pickup location is blacklisted for its truck type (orange), or a
// pickup location group's window is taken that day, so a new job duplicates it
// (orange, confirmed by the dispatcher on submit).
export type JobIssue = "blacklisted" | "typeMismatch" | "duplicate";

const ISSUE_STYLES: Record<
  JobIssue,
  { text: string; hoverBg: string; hintKey: TranslationKey<Namespace.Routes> }
> = {
  blacklisted: {
    text: "text-text-warning",
    hoverBg: "group-hover:bg-surface-warning-light",
    hintKey: "jobBlacklistedForTruckType",
  },
  duplicate: {
    text: "text-text-warning",
    hoverBg: "group-hover:bg-surface-warning-light",
    hintKey: "jobTakenHint",
  },
  typeMismatch: {
    text: "text-text-error",
    hoverBg: "group-hover:bg-surface-error-light",
    hintKey: "jobTypeNotAllowedForTruck",
  },
};

export interface SortableJobRowProps {
  sortId: string;
  displayNumber: number;
  jobId: number | null;
  onOpenPicker: () => void;
  onRemoveJobSlot: () => void;
  getJobDisplayName: (jobId: number | null) => string;
  getJobTypeLabel: (jobId: number) => string | undefined;
  getJobContainersInfo: (jobId: number) => string;
  getJobCustomerName: (jobId: number) => string;
  getJobVolumeText: (jobId: number) => string;
  getJobArrivalTimeInfo: (jobId: number) => {
    arrivalTimeString: string;
    comparison: { status: string; minutes: number; text: string } | null;
  } | null;
  trips: { id: string }[];
  activeTripId: string;
  onMoveJobToTrip: (jobId: number, targetTripId: string | null) => void;
  getJobIssue?: (jobId: number) => JobIssue | null;
  getJobDuplicateNote?: (jobId: number) => string | undefined;
  /** Off where the route has no trips to move a job between (pseudo-routes). */
  showMoveToTrip?: boolean;
}

export const SortableJobRow: React.FC<SortableJobRowProps> = ({
  sortId,
  displayNumber,
  jobId,
  onOpenPicker,
  onRemoveJobSlot,
  getJobDisplayName,
  getJobTypeLabel,
  getJobContainersInfo,
  getJobCustomerName,
  getJobVolumeText,
  getJobArrivalTimeInfo,
  trips,
  activeTripId,
  onMoveJobToTrip,
  getJobIssue,
  getJobDuplicateNote,
  showMoveToTrip = true,
}) => {
  const t = useTypedT(Namespace.Routes);
  const isEmpty = jobId === null;

  const issue = jobId !== null ? (getJobIssue?.(jobId) ?? null) : null;
  const issueStyle = issue ? ISSUE_STYLES[issue] : null;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortId,
    disabled: isEmpty,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const arrival = jobId !== null ? getJobArrivalTimeInfo(jobId) : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-stretch bg-white ${
        isDragging ? "relative z-10 opacity-70" : ""
      }`}
      title={issueStyle ? t(issueStyle.hintKey) : undefined}
    >
      {/* Numbered pin rail. Both connectors grow, so the pin always sits on the
          vertical centre of the row content. The 4px gap goes on the pin side
          only — margins on the outer ends would break the line at every row
          boundary instead of running it straight from pin to pin. The first
          row's upper connector stays invisible but still takes space, so the pin
          does not drift up. */}
      <div className="flex w-12 shrink-0 flex-col items-center border-r border-slate-200">
        <div className={`mb-1 w-px flex-1 ${displayNumber === 1 ? "" : "bg-text-body"}`} />
        <div className="relative flex h-6 w-6 shrink-0 items-center justify-center">
          <IcMapLocationPin className="text-brand-blue h-6 w-6" />
          <span className="absolute top-1 left-1/2 -translate-x-1/2 text-[9px] font-bold text-white">
            {displayNumber}
          </span>
        </div>
        {/* Runs to the next pin — and, on the last row, down to the add-job plus. */}
        <div className="mt-1 w-px flex-1 bg-text-body" />
      </div>

      {/* Content — the hover tint lives here, inset 8px so it never touches the
          rail or the row borders (padding drops by the same 8px to compensate). */}
      <div
        className={`m-2 flex min-w-0 flex-1 items-center gap-3 rounded-lg p-1 transition-colors ${
          issueStyle?.hoverBg ?? "group-hover:bg-slate-100"
        }`}
      >
        {/* Address block */}
        <div className="flex w-[220px] shrink-0 flex-col px-2 py-1 gap-0.5">
          {isEmpty ? (
            <button
              type="button"
              onClick={onOpenPicker}
              className="flex min-w-0 items-center gap-1 text-left focus:outline-none"
            >
              <span className="truncate text-sm text-text-secondary">{t("selectJob")}</span>
              <ChevronDown className="h-4 w-4 shrink-0 text-text-secondary" />
            </button>
          ) : (
            <>
              <span className={`truncate text-sm ${issueStyle?.text ?? "text-text-primary"}`}>
                {getJobDisplayName(jobId)}
              </span>
              {/* A draft has no job number to show yet — it gets one when the
                  route is created, so it says so instead of printing "Z-1". A
                  duplicate adds what it duplicates. */}
              <span className="flex gap-2 text-xs">
                {isDraftJobId(jobId) ? (
                  <span className="font-medium text-brand-blue">{t("jobWillBeCreated")}</span>
                ) : (
                  <span className={issueStyle?.text ?? "text-text-secondary"}>Z{jobId}</span>
                )}
                {issue === "duplicate" && (
                  <span className="font-medium text-text-warning">
                    {getJobDuplicateNote?.(jobId)}
                  </span>
                )}
              </span>
            </>
          )}
        </div>

        {/* Job meta */}
        <div
          className={`flex min-w-0 flex-1 flex-col gap-0.5 text-xs ${
            issueStyle?.text ?? "text-text-secondary"
          }`}
        >
          {jobId !== null && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {getJobContainersInfo(jobId) && <span>{getJobContainersInfo(jobId)}</span>}
                {getJobVolumeText(jobId) && <span>{getJobVolumeText(jobId)}</span>}
                {getJobTypeLabel(jobId) && <span>{getJobTypeLabel(jobId)}</span>}
              </div>
              {getJobCustomerName(jobId) && (
                <span className="truncate">{getJobCustomerName(jobId)}</span>
              )}
              {arrival && (
                <div className="flex flex-row items-center gap-2">
                  <span>{t("arrivalAt", { time: arrival.arrivalTimeString })}</span>
                  {arrival.comparison && (
                    <span
                      className={[
                        "rounded-full px-2 py-0.5 text-[10px] font-semibold text-white",
                        arrival.comparison.status === "early"
                          ? "bg-accent-yellow"
                          : arrival.comparison.status === "late"
                            ? "bg-brand-red"
                            : "bg-brand-green",
                      ].join(" ")}
                    >
                      {arrival.comparison.text}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Actions (revealed on row hover) */}
        <div
          className={`flex shrink-0 items-center gap-2 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 ${
            isDragging ? "opacity-100" : ""
          }`}
        >
          <button
            type="button"
            onClick={onRemoveJobSlot}
            className="text-brand-red"
            aria-label={t("removeJobFromRoute")}
          >
            <IcCancel className="h-6 w-6" />
          </button>
          {!isEmpty && (
            <button
              type="button"
              className="text-text-secondary cursor-grab active:cursor-grabbing"
              aria-label={t("transferJob")}
              {...attributes}
              {...listeners}
              onPointerUp={(e) => {
                e.currentTarget.blur();
              }}
            >
              <DragHandleDots2 className="h-6 w-6 align-center flex justify-center" />
            </button>
          )}
          {!isEmpty && showMoveToTrip && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex size-6 items-center justify-center rounded-md text-text-secondary hover:bg-gray-100 focus:outline-none"
                  aria-label={t("moveToTrip")}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{t("moveToTrip")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {trips.map((trip, idx) => (
                  <DropdownMenuItem
                    key={trip.id}
                    disabled={trip.id === activeTripId}
                    onClick={() => jobId !== null && onMoveJobToTrip(jobId, trip.id)}
                  >
                    {t("tripIndex", { index: idx + 1 })}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="gap-2"
                  onClick={() => jobId !== null && onMoveJobToTrip(jobId, null)}
                >
                  <IcPlus className="h-4 w-4 text-brand-blue" />
                  <span>{t("newTrip")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
};
